/** What the CFO tab's endpoints return. Mirrors apps/api/src/modules/cfo; the server is the source of every figure. */

export interface CheckedFigure {
  label: string;
  value: string;
  currency: string;
  source: string;
  checked: boolean;
}

export interface CfoMessage {
  id: string;
  role: 'user' | 'assistant' | 'fint';
  body: string;
  status: 'waiting' | 'answered' | 'failed';
  figures: CheckedFigure[];
  unchecked: string[];
  links: Array<{ label: string; href: string }>;
  suggestions: Array<{ label: string; href: string }>;
  createdAt: string;
  taskId: string | null;
}

export interface CfoThread {
  id: string;
  kind: 'ask' | 'review';
  title: string;
  period: string | null;
  updatedAt: string;
  waiting: boolean;
  last: string | null;
}

export type InstantIntent = 'cash' | 'owed' | 'late' | 'runway' | 'month' | 'dues' | 'debt';

export interface ThreadList {
  askEnabled: boolean;
  starters: Array<{ intent: InstantIntent; question: string }>;
  threads: CfoThread[];
}

export interface ThreadDetail {
  thread: CfoThread;
  messages: CfoMessage[];
}

export type ChaseStep = 'nudge' | 'polite' | 'firm' | 'final';

export interface ChaseRow {
  invoiceId: string;
  number: string;
  customer: { id: string; name: string; phone: string | null; email: string | null; overseas: boolean };
  invoiceDate: string;
  dueDate: string | null;
  currency: string;
  total: string;
  balance: string;
  daysLate: number;
  state: 'due' | 'resting' | 'waiting' | 'snoozed' | 'held';
  step: ChaseStep | null;
  stepLabel: string | null;
  nextOn: string | null;
  restingUntil: string | null;
  heldUntil: string | null;
  snoozedUntil: string | null;
  history: Array<{ step: string; status: string; channel: string | null; on: string; recipient: string | null }>;
  text: { subject: string; email: string; whatsapp: string } | null;
  wording: 'template' | 'claude';
  ai: null | { taskId: string; status: string; error: string | null };
  msme: null | { interest: string; annualPercent: number; fromDate: string; bankRateNote: string };
}

export interface ChaseList {
  asOf: string;
  currency: string;
  emailEnabled: boolean;
  canSend: boolean;
  ladder: Array<{ step: ChaseStep; offset: number; label: string; enabled: boolean }>;
  skipped: Array<{ id: string; name: string }>;
  rows: ChaseRow[];
}

export interface TaxSettings {
  gstFiling: 'monthly' | 'qrmp' | null;
  entity: 'llp' | 'company' | 'partnership' | 'proprietor' | 'other' | null;
  audited: boolean | null;
  advanceTax: boolean | null;
  deductsTds: boolean | null;
  udyam: string | null;
  msmeCategory: 'micro' | 'small' | 'medium' | null;
}

export interface CfoSettings {
  ask: { enabled: boolean };
  chase: { nudge: boolean; polite: boolean; firm: boolean; final: boolean; skipCustomerIds: string[] };
  tax: TaxSettings;
  canEdit?: boolean;
}

export interface DueView {
  key: string;
  kind: string;
  title: string;
  period: string;
  date: string;
  penalty: string;
  source: string;
  note?: string;
  optional?: boolean;
  status: 'done' | 'overdue' | 'soon' | 'later';
  daysLeft: number;
  estimate: string | null;
  done: null | { doneOn: string; amount: string | null; reference: string | null; byLock: boolean };
}

export interface DuesCalendar {
  asOf: string;
  currency: string;
  from: string;
  to: string;
  tax: TaxSettings;
  questions: string[];
  dues: DueView[];
  canMark: boolean;
}

export interface Finding {
  id: string;
  kind: string;
  title: string;
  detail: string;
  yearly: string | null;
  severity: 'medium' | 'low';
  records: Array<{ type: 'expense' | 'bank'; id: string; date: string; amount: string }>;
  hidden: boolean;
  note: null | { id: string; explanation: string; nextStep?: string; falsePositive?: string };
}

export interface SaveView {
  asOf: string;
  currency: string;
  yearly: string;
  findings: Finding[];
  ai: null | { taskId: string; status: string; error: string | null };
}

export interface DebtRow {
  id: string;
  kind: string;
  lender: string;
  accountId: string | null;
  outstanding: string;
  asOf: string;
  annualRate: string;
  emi: string | null;
  payment: string;
  paymentAssumed: boolean;
  dueDay: number | null;
  endDate: string | null;
  rateType: string;
  creditLimit: string | null;
  note: string | null;
  monthlyInterest: string;
  payoff: { months: number | null; interest: string; clears: boolean };
}

export interface PlanView {
  strategy: 'avalanche' | 'snowball' | 'minimum';
  months: number | null;
  interest: string;
  interestMinor: number;
  clears: boolean;
  order: Array<{ id: string; name: string; month: number | null }>;
}

export interface DebtView {
  asOf: string;
  currency: string;
  debts: DebtRow[];
  totals: { outstanding: string; monthlyInterest: string; monthlyPayment: string };
  plans: null | { extra: string; minimum: PlanView; avalanche: PlanView; snowball: PlanView };
  market: { asOf: string; rows: Array<{ kind: string; range: string }> };
  ai: null | { taskId: string; status: string; error: string | null; text: string | null };
  canEdit: boolean;
}

export interface ConsolidationView {
  currency: string;
  debts: Array<{ id: string; lender: string; outstanding: string; annualRate: string }>;
  principal: string;
  emi: string;
  newInterest: string;
  fee: string;
  feeGst: string;
  prepayment: string;
  oldInterest: string | null;
  oldMonths: number | null;
  oldMonthlyPayment: string;
  saving: string | null;
  breakEvenMonth: number | null;
  effectiveAnnualRate: number;
  tenureMonths: number;
  prepaymentNote: string;
}

export const WAITING_TEXT = 'Waiting for your assistant. It answers when Claude next connects to Fint.';
